/**
 * Wave 4.07 — Searchable CAD Developer combobox with inline “+ Add new”.
 *
 * Mirrors ``CustomerCombobox`` so the CAD tick dialog can pick an existing
 * CAD Developer OR create a brand-new one without leaving the dialog. The
 * selection is OPTIONAL — an operator may tick CAD with only a weight and no
 * developer attribution.
 *
 * Implementation: Popover + filtered scrollable list + a footer button that
 * opens ``CadDeveloperFormDialog`` pre-filled with the current search text.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronsUpDown, Check, Plus, X, PencilRuler, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CadDeveloperFormDialog } from './CadDeveloperFormDialog';

export function CadDeveloperCombobox({
  developers = [],
  value,                  // selected cad_developer_id ('' / null = none)
  onChange,               // (newId, devObject|null) => void
  companyId,
  onDeveloperCreated,     // (dev) => void  — fired AFTER refresh
  placeholder = 'Search or add a CAD developer…',
  allowClear = true,
  loading = false,
  testIdPrefix = 'cad-developer-combobox',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [addDlgOpen, setAddDlgOpen] = useState(false);
  const triggerRef = useRef(null);

  const selected = useMemo(
    () => developers.find((c) => c.id === value) || null,
    [developers, value],
  );

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return developers;
    return developers.filter((c) => {
      const hay = [c.name || '', c.phone || '', c.email || ''].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [developers, query]);

  const trimmed = (query || '').trim();
  const hasExactMatch = trimmed && developers.some(
    (c) => (c.name || '').toLowerCase() === trimmed.toLowerCase(),
  );

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const pick = (c) => {
    onChange(c?.id || '', c || null);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('', null);
  };

  return (
    <>
      <Popover open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal h-9',
              !selected && 'text-slate-500',
            )}
            data-testid={`${testIdPrefix}-trigger`}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <PencilRuler size={14} className="text-slate-400 flex-shrink-0" />
              <span className="truncate">
                {selected ? selected.name : placeholder}
              </span>
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              {selected && allowClear && (
                <X
                  size={14}
                  className="text-slate-400 hover:text-rose-600 cursor-pointer"
                  onClick={clear}
                  data-testid={`${testIdPrefix}-clear`}
                />
              )}
              <ChevronsUpDown size={14} className="text-slate-400" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[420px]"
          align="start"
        >
          <div className="border-b border-[var(--divider)] p-2">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, phone, email…"
              className="h-8 text-sm"
              data-testid={`${testIdPrefix}-search-input`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-xs text-slate-500">
                <Loader2 size={14} className="animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <>
                {allowClear && (
                  <button
                    type="button"
                    onClick={() => pick(null)}
                    className={cn(
                      'w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors border-b border-[var(--divider)]',
                      !value && 'bg-[var(--jewellery-bg)]/40',
                    )}
                    data-testid={`${testIdPrefix}-option-none`}
                  >
                    <Check
                      size={14}
                      className={cn(
                        'mt-1 flex-shrink-0',
                        !value ? 'text-[var(--jewellery-icon)]' : 'invisible',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[var(--ink-navy)] italic">
                        No CAD Developer
                      </div>
                      <div className="text-[10px] text-slate-500">
                        Tick CAD without attributing a developer
                      </div>
                    </div>
                  </button>
                )}
                {filtered.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-500">
                    {developers.length === 0
                      ? 'No CAD developers yet. Use "+ Add" below to create one.'
                      : 'No matches. Refine the search or add a new developer.'}
                  </div>
                ) : (
                  filtered.slice(0, 200).map((c) => {
                    const isSel = c.id === value;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pick(c)}
                        className={cn(
                          'w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors',
                          isSel && 'bg-[var(--jewellery-bg)]/40',
                        )}
                        data-testid={`${testIdPrefix}-option-${c.id}`}
                      >
                        <Check
                          size={14}
                          className={cn(
                            'mt-1 flex-shrink-0',
                            isSel ? 'text-[var(--jewellery-icon)]' : 'invisible',
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[var(--ink-navy)] truncate">
                            {c.name}
                          </div>
                          {(c.phone || c.email) && (
                            <div className="text-[10px] text-slate-500 truncate">
                              {[c.phone, c.email].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </>
            )}
          </div>
          <div className="border-t border-[var(--divider)] p-2 bg-slate-50/60">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setOpen(false); setAddDlgOpen(true); }}
              className="w-full justify-start text-[var(--ink-navy)] hover:bg-white"
              disabled={hasExactMatch}
              data-testid={`${testIdPrefix}-add-new-button`}
            >
              <Plus size={13} className="mr-1.5" />
              {trimmed
                ? hasExactMatch
                  ? `"${trimmed}" already exists`
                  : `Add new CAD developer "${trimmed}"`
                : 'Add new CAD developer'}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <CadDeveloperFormDialog
        open={addDlgOpen}
        onOpenChange={setAddDlgOpen}
        companyId={companyId}
        defaultName={trimmed}
        onCreated={async (created) => {
          if (onDeveloperCreated) await onDeveloperCreated(created);
          if (created?.id) onChange(created.id, created);
        }}
      />
    </>
  );
}

export default CadDeveloperCombobox;
